import { FieldStatus } from 'chaika-enums';
import type * as BackendTypes from 'chaika-types';
import { DatesAsString, Nullable, NumberArrayAsString, StringArrayAsString } from '../backend/common';
import * as BackendEntryTypes from '../backend/entry';
import { EntityFileData } from './ehr';
import { ReferralData } from './referral';
import { ReservationData } from './reservation';

export interface EntryDataUpdateParams extends BackendTypes.EntryDataUpdateParams {}
export interface EntryCreateForm extends Omit<BackendTypes.EntryCreationParams, 'typeId'> {
    /**
     * When undefined (not passed), try to cerate entry with optimal reservationId
     * When null, force create entry without data from reservation
     * When string, use for add default data to entry
     */
    typeId?: string;
    legalEntityId?: string;
    reservation?: Nullable<ReservationData>;
    referral?: Nullable<ReferralData>;
    medicalCaseId?: string;
}
export interface EntryFieldData extends BackendEntryTypes.EntryFieldData {}
export interface UpdateFieldForm {
    field: {
        value: BackendEntryTypes.FieldValueParams;
    };
}
export type FieldValueData = BackendEntryTypes.FieldValueData;
export interface FieldValueItem<T = any> extends BackendEntryTypes.FieldValueItem<T> {}
export interface EntryListQuery
    extends NumberArrayAsString<
        StringArrayAsString<Omit<DatesAsString<BackendTypes.EntryListQuery>, 'orderBy' | 'direction'>>
    > {
    orderBy?: EntryOrder;
}
export interface EntryDrugParams extends BackendEntryTypes.DrugParams {
    status?: FieldStatus;
}
export interface PregnancyParams extends BackendTypes.PregnancyParams {}
export interface EntryVersionsResponse extends BackendEntryTypes.EntryVersionsResponse {}
export interface DentalTreatmentParams extends BackendTypes.DentalTreatmentParams {}
export interface DentalTreatmentStageParams extends BackendTypes.DentalTreatmentStageParams {}
export interface ToothParams extends BackendTypes.ToothParams {}

export interface EntryListResponse extends Omit<BackendTypes.EntryListResponse, 'entries'> {
    entries: EntryListItem[];
}

export interface EntryListItem extends DatesAsString<BackendTypes.EntryListItemViewModel> {}

export interface EntryData extends DatesAsString<BackendTypes.EntryViewModel> {}

export interface EntryArchiveExportResponse extends BackendTypes.EntryArchiveExportResponse {}
export interface EntryArchiveExportStatusResponse extends BackendTypes.EntryArchiveExportStatusResponse {}
export interface EntryEDSResponse extends BackendTypes.EntryCertificateResponse {}
export interface EntryEDSData extends BackendTypes.EntryCertificateViewModel {}

export interface EntryDetailData {
    entryId: string;
    serviceIds?: string[];
    laboratoryServiceIds?: string[];
}

export interface EntryDetailResponse {
    entries: EntryDetailData[];
}

export interface EntryWithRelationsResponse extends Omit<BackendTypes.EntryWithRelationsResponse, 'entry' | 'files'> {
    entry: EntryData;
    files: EntityFileData[];
}

export interface EntryFieldUpdateForm extends UpdateFieldForm {
    id: string;
    name: string;
}

export interface VisitTimeUpdateParams {
    visitTime: string;
}

export interface EntryVersionData extends DatesAsString<BackendTypes.EntryListItemViewModel> {
    fields: EntryFieldData[];
    author: string;
    isUrgent: boolean;
}

export interface EntryVersionResponse {
    versions: EntryVersionData[];
}

export interface EhrEntryCount {
    ehrId: string;
    count: number;
}

export interface EntryReservationResponse {
    optimalReservationId?: string;
    reservations?: ReservationData[];
}

export interface EntryReservationQuery extends DatesAsString<BackendTypes.EntryReservationQuery> {}

export interface EhrsEntryCountResponse {
    ehrsEntryCount: EhrEntryCount[];
}

export interface CreateEntryResponse {
    redirectUrl: string;
}

export enum EntryOrder {
    CreationTime = 'creationTime',
    VisitTime = 'visitTime',
    Urgency = 'isUrgent',
}

export { EntryStatus, FieldStatus, DentalTreatmentStageStatus, DentalTreatmentPlanStatus } from 'chaika-enums';
export type { LegacyData as LegacyDataParams, LegacyValue as LegacyValueParams } from '../backend/entry';
export { AllergenStatus, XmlDataType } from '../backend/entry';

export const enum ENTRY_RESERVATION_INTERVAL {
    From = 7,
}

export interface FileViewModel extends DatesAsString<BackendTypes.FileViewModel> {}

export interface InsuranceApprovalHistory extends DatesAsString<BackendTypes.EntryInsuranceApprovalHistoryResponse> {}
export interface InsuranceApprovalHistoryVersion
    extends DatesAsString<BackendTypes.EntryInsuranceApprovalHistoryVersion> {}

export interface EntryTranslationUpdateForm extends BackendTypes.EntryTranslationUpdateForm {}
export interface EntryTranslationsUpdateForm extends BackendTypes.EntryTranslationsUpdateForm {}
export interface EntryTranslationParams extends BackendTypes.EntryTranslationParams {}
export interface EntryTranslationsParams extends BackendTypes.EntryTranslationsParams {}
export interface EntryTranslationFieldsParams extends BackendTypes.EntryTranslationFieldsParams {}
export interface EntryTranslationStatusUpdateForm extends BackendTypes.EntryTranslationStatusUpdateForm {}
export interface EntryTranslationStatusParams extends BackendTypes.EntryTranslationStatusParams {}

export interface EntryTranslationResponse extends BackendTypes.EntryTranslationResponse {}
export interface EntryTranslationData extends BackendTypes.EntryTranslationViewModel {}
export interface EntryTranslationFieldData extends BackendTypes.EntryTranslationFieldViewModel {}

export interface EntryRenderedServiceStatisticsData extends BackendTypes.EntryRenderedServiceStatisticsViewModel {}

export interface SoapTypeData extends BackendTypes.SoapTypeViewModel {}

export interface EntryPrintHtmlResponse extends BackendTypes.EntryPrintHtmlResponse {}
export interface EntryPrintHtmlCreateParams extends BackendTypes.EntryPrintHtmlCreateParams {}

export interface EntryPrintHtmlData extends BackendTypes.EntryPrintHtmlData {}

export interface EntryAudioRecordCreateParams extends BackendTypes.EntryAudioRecordCreateParams {}
export interface EntryAudioRecordListQuery extends BackendTypes.EntryAudioRecordListQuery {}
export interface EntryAudioRecordUpdateStatusForm extends BackendTypes.EntryAudioRecordUpdateStatusForm {}
export interface EntryAudioRecordUploadStateForm extends BackendTypes.EntryAudioRecordUploadStateForm {}
export interface EntryAudioRecordData extends BackendTypes.EntryAudioRecordViewModel {}
export interface DialogueSessionInitParams extends BackendTypes.DialogueSessionInitForm {}
export interface DialogueSessionFinalizeParams extends BackendTypes.DialogueSessionFinalizeForm {}
export interface DialogueSessionInitResult extends BackendTypes.DialogueSessionInitResult {}
export interface DialogueSessionChunkResult extends BackendTypes.DialogueSessionChunkResult {}
export interface DialogueSessionFinalizeResult extends BackendTypes.DialogueSessionFinalizeResult {}
export interface DialogueSessionCancelParams extends BackendTypes.DialogueSessionCancelForm {}
export interface DialogueSessionCancelResult extends BackendTypes.DialogueSessionCancelResult {}
export interface DialogueSessionStateView extends BackendTypes.DialogueSessionStateView {}
export interface AudioRecordPreviewData extends BackendTypes.AudioRecordPreviewViewModel {}
export interface AudioRecordPreviewFieldData extends BackendTypes.AudioRecordPreviewFieldViewModel {}
export interface AudioRecordPreviewNoteData extends BackendTypes.AudioRecordPreviewNoteViewModel {}
export interface AudioRecordPreviewEpisodeData extends BackendTypes.AudioRecordPreviewEpisodeViewModel {}
export interface AudioRecordPreviewRecommendationData extends BackendTypes.AudioRecordPreviewRecommendationViewModel {}
export interface AudioRecordPreviewFollowUpTaskData extends BackendTypes.AudioRecordPreviewFollowUpTaskViewModel {}
export interface AudioRecordPreviewDrugData extends BackendTypes.AudioRecordPreviewDrugViewModel {}
export interface AudioRecordPreviewReferralData extends BackendTypes.AudioRecordPreviewReferralViewModel {}
export interface AudioRecordPreviewDiagnosisData extends BackendTypes.AudioRecordPreviewDiagnosisViewModel {}
